import React, { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import FormButton from 'components/form-components/FormButton';
import plusIcon from 'assets/images/icons/plus.svg';
import searchIcon from 'assets/images/icons/search.svg';
import importIcon from 'assets/images/icons/import.svg';
import TextField from 'components/form-components/TextField';
import PropTypes from 'prop-types';
import arrowLeft from 'assets/images/icons/arrow-left.svg';
import { useTranslation } from 'react-i18next';
import AddColumnDropdown from './AddColumnDropdown';
import EntriesActions from './EntriesActions';
import Export from 'components/common-components/Export';
import ColumnActions from './ColumnActions';
import { CallAPI } from 'actions/General';
import { geoCoordinatesRegex, phoneRegex, typeOptions } from '../utils';
import toast from 'react-hot-toast';
import { cloneDeep } from 'lodash';
import { notificationKey } from 'config/config';
import { uploadImage } from 'pages/company/utils';

const EditDataSourceHeader = ({
  refreshLists,
  setAddRow,
  title,
  datasource_id,
  dataSourceName,
  onBack,
  fieldsList,
  selectedRows,
  selectedColumns,
  actionsCallBack,
}) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState('');
  const searchChanged = (_, value) => setSearchText(value);
  const [allowRemoveKeyFilter, setAllowRemoveKeyFilter] = useState(false);
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSubmit(file);
      event.target.value = null;
    }
  };

  const validateDataRows = (typeRow, dataRows, dataRowsStartingIndex) => {
    const errors = [];
    dataRows.forEach((row, rowIndex) => {
      row.forEach((value, colIndex) => {
        const type = typeRow[colIndex];
        let isValid = true;

        if (value !== '') {
          switch (type) {
            case 'text':
              isValid = true;
              break;
            case 'number':
              isValid = !isNaN(Number(value));
              break;
            case 'geoCoordinates':
              isValid = geoCoordinatesRegex.test(value);
              break;
            case 'dateTime':
              isValid = !isNaN(new Date(value).getTime());
              break;
            case 'phoneNumber':
              isValid = phoneRegex.test(value);
              break;
            default:
              isValid = false;
          }
        }
        if (!isValid) {
          errors.push(
            `Line ${rowIndex + 1 + dataRowsStartingIndex}, Column ${
              colIndex + 1
            } (type: ${type}): Invalid value '${value}'`,
          );
        }
      });
    });
    return errors;
  };

  const validateCSV = (rows, validColumnTypes) => {
    if (rows.length < 3) return 'File error: Missing header, type, or data rows';
    const [headerRow, typeRow, ...rawDataRows] = rows;
    const dataRowsStartingIndex = 2;
    const dataRows = cloneDeep(rawDataRows);

    // Detect _id column so we can skip it during validation (it's not part of the schema)
    const idColIndex = headerRow.indexOf('_id');
    const hasIdCol = idColIndex !== -1;
    const expectedColCount = fieldsList.length + (hasIdCol ? 1 : 0);

    // check each row has the expected number of columns
    const columnMismatchMessages = [];
    rows.forEach((row, rowIndex) => {
      if (row.length !== expectedColCount) {
        columnMismatchMessages.push(
          `Line ${rowIndex + 1} column count mismatch: expected ${expectedColCount} found ${
            row.length
          }`,
        );
      }
    });
    if (columnMismatchMessages.length) {
      console.log('File error: ', columnMismatchMessages);
      return 'File error: Wrong number of columns';
    }
    // Check if expected header columns are there
    const missingHeaderColumns = [];
    for (let i = 0; i < fieldsList.length; i++) {
      if (!headerRow.includes(fieldsList[i].name)) {
        missingHeaderColumns.push(`Column name '${fieldsList[i].name}' not exist`);
      }
    }
    if (missingHeaderColumns.length) {
      console.log('File error: ', missingHeaderColumns);
      return 'File error: Expected Header columns not found';
    }
    // check for invalid header types
    const typeMismatchMessages = [];
    typeRow.forEach((type, colIndex) => {
      if (!validColumnTypes.includes(type)) {
        typeMismatchMessages.push(`Column ${colIndex + 1} has invalid type '${type}'`);
      }
    });
    if (typeMismatchMessages.length) {
      console.log('File error:', typeMismatchMessages);
      return 'File error: Column types are Invalid';
    }
    // Check that each cell matches its expected data type.
    const validateDataRowsResult = validateDataRows(typeRow, dataRows, dataRowsStartingIndex);
    if (validateDataRowsResult.length) {
      console.log('File error: ', validateDataRowsResult);
      return 'File error: Some rows have invalid data types';
    }

    return null;
  };

  const validateAndSubmit = (file) => {
    const validColumnTypes = typeOptions.map((o) => o.value);

    Papa.parse(file, {
      skipEmptyLines: true,
      complete: async (result) => {
        const error = validateCSV(result.data, validColumnTypes);
        if (error) {
          toast(`${error}`, {
            style: { borderRadius: '8px', background: 'red', color: '#fff' },
          });
          return;
        }
        await submit(file);
      },
      error: (err) => {
        console.log('ERROR', err);
        toast(`Error: in parsing`, {
          style: { borderRadius: '8px', background: 'red', color: '#fff' },
        });
      },
    });
  };

  const submit = async (file) => {
    const fileData = { fileName: `import_data_source_entries_${Date.now()}.csv` };
    const response = await CallAPI('GET_PUBLIC_UPLOAD_URL', { ...fileData }, null, null);
    if (response.status) {
      await uploadImage(file, response.data, null);

      const payload = {
        dataSourceId: datasource_id,
        fileName: fileData.fileName,
        filePathUrl: response.data.readUrl,
        locale: 'en',
      };
      const result = await CallAPI('IMPORT_DATA_SOURCE_ENTRIES', payload, null, null);
      if (result.status) {
        toast(t(notificationKey['data_source_entries_import_started']), {
          style: { borderRadius: '8px', background: '#000', color: '#fff' },
        });
      }
    }
  };

  const getDataSourceFilterData = async () => {
    const payload = { dataSourceId: datasource_id, page: 1, limit: 10 };
    const result = await CallAPI('GET_DATA_SOURCE_FILTER_DATA', payload, null, null, null, null);
    if (result.status) setAllowRemoveKeyFilter(result.data.totalCount === 0);
  };

  useEffect(() => {
    if (datasource_id) getDataSourceFilterData();
  }, [datasource_id]);

  return (
    <div className='section-header d-flex justify-content-between px-2'>
      <div className='d-flex align-items-center gap-3'>
        <div className='d-flex align-items-center gap-3' onClick={() => onBack()}>
          <img src={arrowLeft} className='icon back-arrow' />
          <label className='theme-size-1_3 theme-font-inter-semi-bold'>{t(title)}</label>
        </div>
        {selectedColumns.length > 0 ? (
          <ColumnActions
            columns={selectedColumns}
            dataSourceId={datasource_id}
            dataSourceName={dataSourceName}
            fieldsList={fieldsList}
            refreshLists={refreshLists}
            allowRemoveKeyFilter={allowRemoveKeyFilter}
          />
        ) : (
          <EntriesActions
            ids={selectedRows}
            isActionButton={false}
            actionsCallBack={actionsCallBack}
            actionData={{
              datasourceName: title,
              row_datasource_id: datasource_id,
            }}
            type={'data_sources_entries'}
          />
        )}
      </div>
      <div className='d-flex align-items-center gap-3'>
        <TextField
          value={searchText}
          name='search'
          handleChange={searchChanged}
          placeholder={t('field_search')}
          icon={searchIcon}
          classes='mb-0'
          shrink
        />

        <AddColumnDropdown
          datasource_id={datasource_id}
          refreshLists={refreshLists}
          fieldsList={fieldsList}
        />

        <div className='d-flex gap-3 align-items-center'>
          <FormButton
            text='button_import'
            onClick={handleUploadClick}
            variant='green-2'
            icon={importIcon}
          />
          <input
            className='d-none'
            ref={fileInputRef}
            type='file'
            accept='.csv'
            onChange={handleFileChange}
          />
        </div>

        <Export
          type={'data_source'}
          screenTab={'database'}
          additionalDetail={{
            dataSourceId: datasource_id,
            dataSourceName: title,
            exportMode: 'all',
          }}
        />

        <FormButton
          text='button_create_row'
          onClick={() => setAddRow(true)}
          variant='green-2'
          icon={plusIcon}
        />
      </div>
    </div>
  );
};
EditDataSourceHeader.propTypes = {
  title: PropTypes.string.isRequired,
  datasource_id: PropTypes.string.isRequired,
  dataSourceName: PropTypes.string.isRequired,
  setAddRow: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  refreshLists: PropTypes.func.isRequired,
  fieldsList: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  selectedColumns: PropTypes.array.isRequired,
  actionsCallBack: PropTypes.func.isRequired,
};
export default EditDataSourceHeader;
